/*
 * HomePanel — Admin's landing page.
 *
 * KPI tiles + escalation feed. Replaces the "land on Providers" default.
 * Every card either shows a real KPI or links to the deep page.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", bone: "#F9F8F6", amber: "#B45309", red: "#B91C1C" };

function Kpi({ label, value, hint, onClick, testid, tint = C.ink }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="rounded-2xl bg-white border p-4 text-left hover:shadow-md transition-shadow"
      style={{ borderColor: C.line }}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: C.dim }}>{label}</div>
      <div className="text-3xl font-bold mt-1" style={{ color: tint }}>{value}</div>
      {hint && <div className="text-xs mt-1" style={{ color: C.dim }}>{hint}</div>}
    </button>
  );
}

export default function HomePanel({ onNavigate }) {
  const [kpi, setKpi] = useState({ trips_today: "—", in_progress: "—", pending_apps: "—", escalations: "—" });
  const [escalations, setEscalations] = useState([]);
  const [sosEvents, setSosEvents] = useState([]);
  const [swaig, setSwaig] = useState({ calls_today: 0, last_at: null });
  const [loading, setLoading] = useState(true);

  // Plain-English escalation reason — riders/dispatchers should never see raw
  // internal statuses (Bible §"Zero developer terminology"). Falls back to the
  // status only if none of the known reasons match.
  const reasonFor = (t) => {
    const s = t.status || "";
    if (t.escalation_reason) return String(t.escalation_reason);
    if (s === "NO_SHOW") return "Driver reported no-show — needs review";
    if (s === "DISPUTE") return "Rider disputed the trip";
    if (["CANCELLED_AFTER_ARRIVAL", "CANCELED_AFTER_ARRIVAL"].includes(s)) return "Cancelled after driver arrived";
    if (s === "REASSIGN_NEEDED") return "Assigned driver dropped — needs reassignment";
    if (s === "OFFER_EXHAUSTED") return "No nearby driver accepted — needs manual assign";
    if (["REQUESTED", "OFFERED", "OFFERED_TP"].includes(s)) {
      const mins = Math.round((Date.now() - (Date.parse(t.created_at || "") || 0)) / 60000);
      return `Unassigned for ${mins} min — nudge a driver`;
    }
    return s;
  };

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        // Fire in parallel — each is non-fatal
        const [tripsR, appsR, driverAppsR, partnerLeadsR, sosR, swaigR] = await Promise.allSettled([
          apiClient.get("/tpt/trips?limit=200"),
          apiClient.get("/tpt/applications?state=pending").catch(() => ({ data: [] })),
          apiClient.get("/tpt/drivers/new-applications").catch(() => ({ data: [] })),
          apiClient.get("/partners/leads").catch(() => ({ data: [] })),
          apiClient.get("/rider/admin/sos/active").catch(() => ({ data: { items: [] } })),
          apiClient.get("/swaig/calls?limit=50").catch(() => ({ data: [] })),
        ]);
        const unwrap = (r, key) => {
          if (r.status !== "fulfilled") return [];
          const d = r.value.data;
          if (Array.isArray(d)) return d;
          if (Array.isArray(d?.items)) return d.items;
          if (key && Array.isArray(d?.[key])) return d[key];
          return [];
        };
        const trips = unwrap(tripsR);
        const tpApps = unwrap(appsR);
        const driverApps = unwrap(driverAppsR);
        const partnerLeads = unwrap(partnerLeadsR).filter(l => l.status === "new");
        const sos = unwrap(sosR);
        const swaigCalls = unwrap(swaigR);
        const today = new Date().toISOString().slice(0, 10);
        const swaigToday = swaigCalls.filter(c => (c.at || "").slice(0, 10) === today).length;
        const lastAt = swaigCalls[0]?.at || null;
        const tripsToday = trips.filter(t => (t.created_at || "").slice(0, 10) === today).length;
        const inProgress = trips.filter(t => ["ACCEPTED", "EN_ROUTE", "ARRIVED_PICKUP", "ARRIVED_PU", "ON_TRIP"].includes(t.status)).length;
        // GW-5 escalation detection:
        //  - explicit escalation statuses
        //  - stale REQUESTED / OFFERED trips (>4 min in that state, no driver assigned)
        //  - anything with escalation_reason set
        const now = Date.now();
        const STALE_MS = 4 * 60 * 1000;
        const escs = trips
          .filter(t => {
            const s = t.status || "";
            if (["NO_SHOW", "DISPUTE", "CANCELLED_AFTER_ARRIVAL", "CANCELED_AFTER_ARRIVAL",
                 "REASSIGN_NEEDED", "OFFER_EXHAUSTED"].includes(s)) return true;
            if (t.escalation_reason) return true;
            if (["REQUESTED", "OFFERED", "OFFERED_TP"].includes(s) && !t.assigned_driver_id && !t.assigned_driver) {
              const created = Date.parse(t.created_at || "") || 0;
              if (created && (now - created) > STALE_MS) return true;
            }
            return false;
          })
          .slice(0, 12);
        if (!alive) return;
        setKpi({
          trips_today: tripsToday, in_progress: inProgress,
          pending_apps: tpApps.length + driverApps.length + partnerLeads.length, escalations: escs.length + sos.length,
        });
        setEscalations(escs);
        setSosEvents(sos);
        setSwaig({ calls_today: swaigToday, last_at: lastAt });
      } finally { alive && setLoading(false); }
    }
    load();
    // Poll SOS every 15s — this is a safety-critical loop
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  async function resolveSos(sosId) {
    if (!window.confirm("Mark this SOS resolved? Only do this after speaking with the rider.")) return;
    try {
      await apiClient.post(`/rider/admin/sos/${sosId}/resolve`, { resolution_notes: "Resolved from Home dashboard" });
      setSosEvents(prev => prev.filter(s => s.id !== sosId));
    } catch (e) { /* silent — reload will retry */ }
  }

  // GW-5 inline resolver — 3 taps from escalation to reassignment.
  // 1) tap Suggest → fetch /admin/trips/{id}/suggest-drivers
  // 2) tap a candidate → POST /tpt/trips/{id}/assign
  // 3) row auto-collapses on success
  const [expanded, setExpanded] = useState(null);
  const [candidates, setCandidates] = useState({});
  const [candLoading, setCandLoading] = useState(false);
  const [assigningId, setAssigningId] = useState(null);

  async function toggleSuggest(trip) {
    if (expanded === trip.trip_id) { setExpanded(null); return; }
    setExpanded(trip.trip_id);
    if (candidates[trip.trip_id]) return;  // cached
    setCandLoading(true);
    try {
      const r = await apiClient.get(`/admin/trips/${trip.trip_id}/suggest-drivers`);
      const body = r.data?.data || r.data || {};
      const list = body.candidates || [];
      setCandidates(prev => ({ ...prev, [trip.trip_id]: list }));
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.detail || e?.message || "Suggest failed";
      setCandidates(prev => ({ ...prev, [trip.trip_id]: { __error: String(msg) } }));
    } finally { setCandLoading(false); }
  }

  async function assignCandidate(trip, cand) {
    setAssigningId(cand.driver_id);
    try {
      await apiClient.post(`/tpt/trips/${trip.trip_id}/assign`, {
        tp_id: cand.tp_id || trip.assigned_tp?.tp_id,
        driver_id: cand.driver_id,
      });
      setExpanded(null);
      setEscalations(prev => prev.filter(t => t.trip_id !== trip.trip_id));
      setKpi(prev => ({ ...prev, escalations: Math.max(0, (prev.escalations || 0) - 1) }));
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Assign failed";
      alert(msg);
    } finally { setAssigningId(null); }
  }

  return (
    <div className="space-y-6" data-testid="admin-home">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: C.ink }}>Today at MedexOVA</h1>
        <div className="text-sm" style={{ color: C.dim }}>What needs your attention right now.</div>
      </div>

      {sosEvents.length > 0 && (
        <div
          data-testid="sos-priority-card"
          className="rounded-2xl p-4 space-y-3"
          style={{
            background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
            border: "2px solid #DC2626",
            boxShadow: "0 8px 32px rgba(220, 38, 38, 0.25)",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 24 }}>🚨</span>
              <div>
                <div className="text-lg font-black" style={{ color: "#7F1D1D" }}>
                  Active SOS · {sosEvents.length} alert{sosEvents.length === 1 ? "" : "s"} need help
                </div>
                <div className="text-xs" style={{ color: "#991B1B" }}>Call them first. Escalate to 911 if needed.</div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {sosEvents.map(s => {
              const isDriver = s.origin === "driver";
              const name = isDriver ? (s.driver_name || "Unknown driver") : (s.rider_name || "Unknown rider");
              const phone = isDriver ? s.driver_phone : s.rider_phone;
              return (
              <div key={s.id} className="rounded-lg p-3 flex items-center gap-3 flex-wrap"
                style={{ background: "#fff", border: "1px solid #FCA5A5" }}
                data-testid={`sos-row-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold" style={{ color: "#7F1D1D" }}>
                    {name}
                    <span className="ml-2 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded"
                      style={{ background: isDriver ? "#DBEAFE" : "#FCE7F3", color: isDriver ? "#1E40AF" : "#9D174D" }}>
                      {isDriver ? "driver" : "rider"}
                    </span>
                    {phone && <span className="ml-2 text-slate-600 font-normal">· {phone}</span>}
                  </div>
                  <div className="text-xs" style={{ color: C.dim }}>
                    {new Date(s.at).toLocaleTimeString()} · trip {s.trip_id ? s.trip_id.slice(0, 8) : "—"}
                    {s.note ? ` · "${s.note}"` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {phone && (
                    <a href={`tel:${phone}`} data-testid={`sos-call-${s.id}`}
                      className="text-xs font-bold px-3 py-1.5 rounded-full text-white"
                      style={{ background: "#DC2626" }}>📞 Call {isDriver ? "driver" : "rider"}</a>
                  )}
                  {s.lat != null && s.lng != null && (
                    <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer"
                      data-testid={`sos-map-${s.id}`}
                      className="text-xs font-bold px-3 py-1.5 rounded-full"
                      style={{ background: "#fff", border: "1px solid #DC2626", color: "#7F1D1D" }}>Map</a>
                  )}
                  <button onClick={() => resolveSos(s.id)}
                    data-testid={`sos-resolve-${s.id}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full"
                    style={{ background: "#fff", border: "1px solid #94A3B8", color: "#334155" }}>Resolve</button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Trips today" value={loading ? "…" : kpi.trips_today} onClick={() => onNavigate?.("live-ops")} testid="kpi-trips-today" />
        <Kpi label="In progress" value={loading ? "…" : kpi.in_progress} tint={C.forest} onClick={() => onNavigate?.("live-ops")} testid="kpi-in-progress" />
        <Kpi label="Pending applications" value={loading ? "…" : kpi.pending_apps} tint={kpi.pending_apps > 0 ? C.amber : C.ink} onClick={() => onNavigate?.("credentialing-hub")} testid="kpi-pending-apps" />
        <Kpi label="Escalations" value={loading ? "…" : kpi.escalations} tint={kpi.escalations > 0 ? C.red : C.ink} testid="kpi-escalations" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button data-testid="quick-send-onboarding" onClick={() => onNavigate?.("outreach")}
          className="rounded-2xl border-2 border-dashed p-4 text-left hover:bg-white transition-colors"
          style={{ borderColor: C.forest, color: C.forest }}>
          <div className="text-xs uppercase tracking-[0.16em] font-bold">Quick action</div>
          <div className="text-lg font-bold mt-1">📨 Send onboarding link</div>
          <div className="text-xs mt-1" style={{ color: C.dim }}>Text or email an application URL to a caller.</div>
        </button>
        <button data-testid="quick-approve" onClick={() => onNavigate?.("credentialing-hub")}
          className="rounded-2xl border-2 border-dashed p-4 text-left hover:bg-white transition-colors"
          style={{ borderColor: C.forest, color: C.forest }}>
          <div className="text-xs uppercase tracking-[0.16em] font-bold">Queue</div>
          <div className="text-lg font-bold mt-1">✅ Approve pending</div>
          <div className="text-xs mt-1" style={{ color: C.dim }}>Review TPs, drivers, riders awaiting action.</div>
        </button>
        <button data-testid="quick-live-ops" onClick={() => onNavigate?.("live-ops")}
          className="rounded-2xl border-2 border-dashed p-4 text-left hover:bg-white transition-colors"
          style={{ borderColor: C.forest, color: C.forest }}>
          <div className="text-xs uppercase tracking-[0.16em] font-bold">Live</div>
          <div className="text-lg font-bold mt-1">🚦 Live trip board</div>
          <div className="text-xs mt-1" style={{ color: C.dim }}>Real-time trips in flight.</div>
        </button>
      </div>

      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold" style={{ color: C.ink }}>Escalations</div>
          <div className="text-xs" style={{ color: C.dim }}>{escalations.length} items</div>
        </div>
        {escalations.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: C.dim }}>✅ Nothing needs your attention right now.</div>
        ) : (
          <ul className="space-y-1 astat-fade-stagger" key={escalations.length}>
            {escalations.map(t => {
              const isOpen = expanded === t.trip_id;
              const cands = candidates[t.trip_id];
              const err = cands && cands.__error;
              const list = Array.isArray(cands) ? cands : [];
              return (
                <li key={t.trip_id} className="border-b last:border-b-0" style={{ borderColor: C.line }} data-testid={`esc-row-${t.trip_id}`}>
                  <div className="flex items-center justify-between py-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{t.rider_name || t.passenger_name || "—"}</div>
                      <div className="text-xs mt-0.5" style={{ color: C.red }} data-testid={`esc-reason-${t.trip_id}`}>{reasonFor(t)}</div>
                      <div className="text-[11px] truncate mt-0.5" style={{ color: C.dim }}>{t.pickup_address || (t.pickup || {}).address}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleSuggest(t)}
                        data-testid={`esc-suggest-${t.trip_id}`}
                        className="text-xs font-semibold px-3 py-1 rounded-full"
                        style={{
                          background: isOpen ? C.forest : "#fff",
                          color: isOpen ? "#fff" : C.forest,
                          border: `1px solid ${C.forest}`,
                          transition: "all 200ms ease",
                        }}>
                        {isOpen ? "Hide" : "Suggest driver"}
                      </button>
                      <button className="text-xs font-semibold" style={{ color: C.dim }}
                        onClick={() => onNavigate?.("live-ops")}
                        data-testid={`esc-open-${t.trip_id}`}>Open →</button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="pb-3" data-testid={`esc-suggest-panel-${t.trip_id}`}>
                      {candLoading && !cands ? (
                        <div className="text-xs py-2" style={{ color: C.dim }}>Finding closest drivers…</div>
                      ) : err ? (
                        <div className="text-xs py-2" style={{ color: C.red }} data-testid={`esc-suggest-err-${t.trip_id}`}>
                          {err}
                        </div>
                      ) : list.length === 0 ? (
                        <div className="text-xs py-2" style={{ color: C.dim }} data-testid={`esc-suggest-empty-${t.trip_id}`}>
                          No qualified drivers online right now. Try Broadcast from Live Ops.
                        </div>
                      ) : (
                        <ul className="space-y-1">
                          {list.slice(0, 3).map(c => (
                            <li key={c.driver_id} className="flex items-center justify-between rounded-md px-2 py-1.5"
                              style={{ background: C.bone }} data-testid={`esc-cand-${t.trip_id}-${c.driver_id}`}>
                              <div className="min-w-0">
                                <div className="text-sm font-semibold">{c.name} <span className="text-xs font-normal" style={{ color: C.dim }}>· {c.distance_mi} mi</span></div>
                                <div className="text-[11px]" style={{ color: c.production_ready ? C.forest : C.amber }}>
                                  {c.production_ready ? "✓ Ready to accept" : c.readiness_note || "Not fully credentialed"}
                                </div>
                              </div>
                              <button
                                disabled={!c.production_ready || assigningId === c.driver_id}
                                onClick={() => assignCandidate(t, c)}
                                data-testid={`esc-assign-${t.trip_id}-${c.driver_id}`}
                                className="text-xs font-bold px-3 py-1 rounded-full"
                                style={{
                                  background: c.production_ready ? C.forest : "#94A3B8",
                                  color: "#fff",
                                  opacity: assigningId === c.driver_id ? 0.6 : 1,
                                  cursor: c.production_ready ? "pointer" : "not-allowed",
                                }}>
                                {assigningId === c.driver_id ? "Assigning…" : "Assign →"}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <div className="rounded-2xl bg-white border p-4" style={{ borderColor: C.line }} data-testid="admin-voice-ai-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 18 }}>🎙️</span>
            <div>
              <div className="text-sm font-bold" style={{ color: C.ink }}>Voice AI (SWAIG)</div>
              <div className="text-xs" style={{ color: C.dim }}>
                {swaig.calls_today} call{swaig.calls_today === 1 ? "" : "s"} today
                {swaig.last_at ? ` · last activity ${new Date(swaig.last_at).toLocaleTimeString()}` : " · no activity yet"}
              </div>
            </div>
          </div>
          <SwaigTestCallButton />
        </div>
      </div>
    </div>
  );
}

// Places a REAL outbound SWAIG call to a phone number the admin provides.
// Replaces the old `tel:` link that did nothing on desktop.
function SwaigTestCallButton() {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const onClick = async () => {
    const raw = window.prompt(
      "Enter a phone number (E.164, e.g. +15551234567). SWAIG will call it now with a test message."
    );
    if (!raw) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await apiClient.post("/swaig/test-outbound-call", { to_phone: raw.trim() });
      setMsg({ tone: "ok", text: `✓ Calling ${data.to_phone} · call ${data.call_id?.slice(0, 8) || "queued"}` });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsg({ tone: "err", text: detail || "SWAIG call failed. Check SignalWire config." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        data-testid="admin-voice-ai-testcall"
        className="text-xs font-bold px-3 py-1.5 rounded-full"
        style={{ background: C.forest, color: "#fff", opacity: busy ? 0.6 : 1 }}
      >
        {busy ? "Dialing…" : "Test call"}
      </button>
      {msg && (
        <div
          data-testid="admin-voice-ai-testcall-msg"
          className="text-[11px] font-semibold"
          style={{ color: msg.tone === "ok" ? "#047857" : "#B91C1C" }}
        >
          {msg.text}
        </div>
      )}
    </div>
  );
}